import { useState, useMemo } from 'react';

// Utils
import groupOptionsByOptGroup from '../../utils/optGroup/optGroup';

// Use cases
import { addIdsToAddFormUrls, filterAddFormUrlsBySearch } from './addElementUseCase';

/**
 * @typedef {object} useAddElementReturn
 * @property {boolean} popoverAddElementIsOpen - Whether the add element popover is open
 * @property {(isOpen: boolean) => void} setPopoverAddElementIsOpen - Set the add element popover open state
 * @property {string} searchValue - The current search value
 * @property {(value: string) => void} setSearchValue - Set the search value
 * @property {Array<{lib: string, options: Array<AddFormUrlWithId>}>} addFormUrlsGrouped - The add form URLs grouped by optGroup and filtered by search
 */

/**
 * Hook to manage add element functionality (popover, search, grouping)
 * @param {object} props
 * @param {Array<AddFormUrl>} [props.addFormUrls] - The add form URLs from the API
 * @returns {useAddElementReturn} Add element state and actions
 */
const useAddElement = ({ addFormUrls = [] }) => {
  const [popoverAddElementIsOpen, setPopoverAddElementIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  /**
   * @description The add form URLs grouped by optGroup and filtered by search
   * @type {Array<{lib: string, options: Array<AddFormUrlWithId>}>}
   */
  const addFormUrlsGrouped = useMemo(() => {
    const withIds = addIdsToAddFormUrls(addFormUrls);
    const filtered = filterAddFormUrlsBySearch(withIds, searchValue);
    return groupOptionsByOptGroup(filtered);
  }, [addFormUrls, searchValue]);

  return {
    popoverAddElementIsOpen,
    setPopoverAddElementIsOpen,
    searchValue,
    setSearchValue,
    addFormUrlsGrouped,
  };
};

export default useAddElement;
